<template>

    <v-page>

        <transition name="modal">
            <v-modal-customer-create v-if="showModal === 'create'" @saved="reload" />
            <v-modal-customer-delete-all v-if="showModal === 'delete-all'" :ids="selected" @deleted="reload" />
            <v-modal-customer-segment-all v-if="showModal === 'segment-all'" :ids="selected" @saved="reload" />
            <v-modal-customer-tag-all v-if="showModal === 'tag-all'" :ids="selected" @saved="reload" />
            <v-modal-customer-untag-all v-if="showModal === 'untag-all'" :ids="selected" :untag="true" @saved="reload" />
        </transition>

        <v-page-header v-if="pagination.total" :title="pagination | paginationTitle('Customers (', ')')" icon="fas fa-id-card" />
        <v-page-header v-else title="Customers" icon="fas fa-id-card" />

        <v-page-filters>
            <v-button-toolbar v-if="!selected.length" size="sm">
                <v-select size="sm" :model="params.trait" @input="params.trait = $event">
                    <option value="">Name or Email</option>
                    <option v-for="trait in traits" :key="trait.id" :value="trait.field">{{ trait.name | truncate(30) }}</option>
                </v-select>
                <v-searchbar ref="search" :model="params.q" :action="search" />
                <v-button v-if="params.segment_id" size="sm" btn-style="white" @click.native="editSegment">Edit Segment</v-button>
            </v-button-toolbar>
            <v-dropdown v-else
                type="white"
                label="Bulk Actions"
                :items="[
                    { key: 'segment-all', value: 'Segment Selected' },
                    { key: 'tag-all', value: 'Tag Selected' },
                    { key: 'untag-all', value: 'Untag Selected' },
                    { key: 'delete-all', value: 'Delete Selected' }
                ]"
                :show-selected="false"
                @change="onBulkActionSelected">
            </v-dropdown>
            <v-button-toolbar size="sm">
                <v-traits-selector :menu-right="true" />
                <v-pagination :count="pagination.last_page" @search="paginateCustomers($event)" />
                <v-toggle :view="view" @input="view = $event" />
                <v-button-icon @click.native="$store.commit('showModal', 'create')" />
            </v-button-toolbar>
        </v-page-filters>

        <v-page-body :class="Object.keys(pagination).length === 0 ? '' : 'd-flex p-0'">
            <div class="segments">
                <v-page-body-sidebar>
                    <a class="nav-link" :class="{ 'active': !$route.query.segment_id }" href="#" @click.prevent="selectSegment(null)">
                        <span class="mr-10">All Customers</span>
                        <span class="text-muted">{{ project.customers_count | numeral('0,0') }}</span>
                    </a>
                    <a v-for="segment in segments" :key="segment.id" class="nav-link" :class="{ 'active': $route.query.segment_id === segment.id }" href="#" :title="segment.name" @click.prevent="selectSegment(segment.id)">
                        <span class="mr-10">{{ segment.name }}</span>
                        <span class="text-muted">{{ segment.customers_count | numeral('0,0') }}</span>
                    </a>
                </v-page-body-sidebar>
            </div>
            <div class="flex-grow-1" :class="{ 'p-15': view === 'list' && !customers.length }">
                <v-notice v-if="view === 'list' && !customers.length"
                    title="No Customers Found"
                    message="Waiting for customers to be added to your project.">
                </v-notice>
                <v-customers-list ref="customers" v-show="customers.length && view === 'list'" :customers="customers" @selected="onSelectedRows" />
                <v-customers-map v-show="view === 'map'" :show="view === 'map'" :customers="customers" />
            </div>
        </v-page-body>

    </v-page>

</template>

<style scoped lang="scss">

    .cms-primary {
        height: 100vh;
    }

    .segments {
        display: flex;
        flex-grow: 1;
        flex-shrink: 0;
        max-width: 250px;
        position: relative;
        .page-body-sidebar {
            height: 100%;
            left: 0;
            max-width: 250px;
            position: absolute;
            top: 0;
        }
    }

</style>

<script>

import axios from 'axios';
import CustomersList from './components/CustomersList';
import CustomersMap from './components/CustomersMap';
import ModalCustomerCreate from '@modals/customer/ModalCustomerCreate';
import ModalCustomerDeleteAll from '@modals/customer/ModalCustomerDeleteAll';
import ModalCustomerSegmentAll from '@modals/customer/ModalCustomerSegmentAll';
import ModalCustomerTagAll from '@modals/customer/ModalCustomerTagAll';
import ProjectApi from '@api/ProjectApi';
import Toggle from './components/Toggle';

export default {

    components: {
        'v-customers-list': CustomersList,
        'v-customers-map': CustomersMap,
        'v-modal-customer-create': ModalCustomerCreate,
        'v-modal-customer-delete-all': ModalCustomerDeleteAll,
        'v-modal-customer-segment-all': ModalCustomerSegmentAll,
        'v-modal-customer-tag-all': ModalCustomerTagAll,
        'v-modal-customer-untag-all': ModalCustomerTagAll,
        'v-toggle': Toggle,
    },

    data() {
        return {
            segments: [],
            customers: [],
            pagination: {},
            view: 'list',
            params: {},
            timer: null, // For polling.
            selected: [], // For performing actions on multiple customers.
        }
    },

    computed: {

        traits() {
            if (!this.$store.getters.project.traits) return [];
            return this.$store.getters.project.traits
                .filter(t => {
                    return t.visibility.list && t.type === 'string';
                })
                .sort((a, b) => {
                    if (a.name < b.name) { return -1; }
                    if (a.name > b.name) { return 1; }
                    return 0;
                });
        },

    },

    mounted() {
        document.getElementsByTagName('body')[0].classList.add('fluid');
        this.params = this.$route.query;
        this.reload();
    },

    watch: {

        $route(val) {
            this.params = val.query;
            this.getCustomers();
        },

        customers(val) {
            // Update sidebar counts
            if (!this.segments.length) return;
            if (!this.$route.query.segment_id && !this.$route.query.q) {
                this.project.customers_count = this.pagination.total;
            } else if (this.$route.query.segment_id && !this.$route.query.q) {
                let segment = this.segments.find(s => s.id === this.$route.query.segment_id);
                if (segment) {
                    let index = this.segments.indexOf(segment);
                    this.segments[index].customers_count = this.pagination.total;
                }
            }
        },

    },

    methods: {

        onSelectedRows(val) {
            clearInterval(this.timer);
            this.selected = val;
        },

        onBulkActionSelected(item) {
            this.$store.commit('showModal', item.key);
        },

        reset() {
            clearInterval(this.timer);
            if (this.$refs.customers) {
                this.$refs.customers.deselectAll();
            }
        },

        reload() {
            this.reset();
            this.$store.commit('loading', true);
            let requests = [
                ProjectApi.getProject(this.project.id),
                ProjectApi.getSegments(this.project.id),
                ProjectApi.getCustomers(this.project.id, this.params),
            ];
            axios.all(requests)
                .then(axios.spread((resProject, resSegments, resCustomers) => {
                    this.$store.commit('loading', false);
                    this.segments = resSegments.data.data;
                    this.customers = resCustomers.data.data;
                    this.pagination = resCustomers.data.pagination;
                    this.$events.$emit('pageLoaded');
                    // Begin polling.
                    this.timer = setInterval(() => {
                        if (this.$route.name === 'customers' && this.view === 'list') {
                            this.getCustomers(false);
                        }
                    }, 60000);
                }), () => {
                    this.pagination = {};
                    this.$store.commit('loading', false);
                    this.$events.$emit('pageError');
                });
        },

        getCustomers(top = true) {
            this.reset();
            this.$store.commit('loading', true);
            ProjectApi.getCustomers(this.project.id, this.params)
                .then((response) => {
                    this.$store.commit('loading', false);
                    this.customers = response.data.data;
                    this.pagination = response.data.pagination;
                    // Scroll to top?
                    if (top) this.scrollToTop();
                    // Begin polling.
                    this.timer = setInterval(() => {
                        if (this.$route.name === 'customers' && this.view === 'list') {
                            this.getCustomers(false);
                        }
                    }, 60000);
                }, (error) => {
                    this.$store.commit('loading', false);
                    this.$store.commit('setAlertWithError', error);
                });
        },

        paginateCustomers(page) {
            let query = Object.assign({}, this.$route.query);
            query.page = page;
            this.$router.replace({ query: query }).catch(() => {});
        },

        scrollToTop() {
            let el = document.getElementsByClassName('customers')[0];
            el.scrollTo(0, 0);
        },

        editSegment() {
            this.$router.push({
                name: 'segments-edit',
                params: { 'id': this.params.segment_id },
            });
        },

        selectSegment(id = null) {
            let query = Object.assign({}, this.$route.query);
            query.page = 1;
            query.segment_id = id;
            this.$router.replace({ query: query }).catch(() => {});
        },

        search() {
            let query = Object.assign({}, this.$route.query);
            query.page = 1;
            query.q = this.$refs.search.val;
            this.$router.replace({ query: query }).catch(() => {
                this.reload();
            });
        },

    },

    beforeDestroy() {
        clearInterval(this.timer);
    },

    destroyed() {
        document.getElementsByTagName('body')[0].classList.remove('fluid');
    },

}

</script>
